import ChevronRight from "@mui/icons-material/ChevronRight";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Box,
  Collapse,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { SKILL_ENTRYPOINT } from "encamp-shared/src/s3/skills/paths";

import type { SkillFileTreeNode } from "./buildSkillFileTree";
import type { SkillEntryKind } from "./skillFilePaths";
import type { SkillTreeEntryEditing } from "./useSkillTreeEntryEditing";

import { SkillTreeNameInput } from "./SkillTreeNameInput";

const RENAME_BUTTON_CLASS = "skill-tree-rename";

/** Left padding for a row at `depth`; files sit past the folder chevron. */
function rowIndent(depth: number, kind: SkillEntryKind): number {
  return 1 + depth * 1.5 + (kind === "file" ? 3.25 : 0);
}

function EntryIcon({ kind }: { kind: SkillEntryKind }) {
  return (
    <ListItemIcon sx={{ minWidth: 28 }}>
      {kind === "folder" ? (
        <FolderOutlined sx={{ fontSize: 18 }} />
      ) : (
        <InsertDriveFileOutlined sx={{ fontSize: 18 }} />
      )}
    </ListItemIcon>
  );
}

/** An entry whose name is being typed: a new one, or one being renamed. */
function NamingRow({
  kind,
  depth,
  initialName,
  editing,
}: {
  kind: SkillEntryKind;
  depth: number;
  initialName: string;
  editing: SkillTreeEntryEditing;
}) {
  return (
    <ListItem dense sx={{ pl: rowIndent(depth, kind), pr: 1, py: 0.25 }}>
      <EntryIcon kind={kind} />
      <SkillTreeNameInput
        kind={kind}
        initialName={initialName}
        error={editing.error}
        // An unnamed new file keeps its row on blur, so its editor text survives.
        keepOnEmptyBlur={editing.namingNewFile}
        onChange={editing.clearError}
        onCommit={editing.commit}
        onCancel={editing.cancel}
      />
    </ListItem>
  );
}

function RenameButton({ onClick, name }: { onClick: () => void; name: string }) {
  return (
    <IconButton
      className={RENAME_BUTTON_CLASS}
      size="small"
      aria-label={`Rename ${name}`}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      sx={{ p: 0.25, ml: 0.5 }}
    >
      <EditOutlined sx={{ fontSize: 16 }} />
    </IconButton>
  );
}

/**
 * The rename pencil shows on hover or keyboard focus. Transparent rather than
 * hidden, so it stays reachable by Tab and screen readers.
 */
const rowHoverSx = {
  [`& .${RENAME_BUTTON_CLASS}`]: { opacity: 0 },
  [`&:hover .${RENAME_BUTTON_CLASS}, & .${RENAME_BUTTON_CLASS}:focus-visible`]: { opacity: 1 },
};

type SkillFileTreeRowsProps = {
  nodes: readonly SkillFileTreeNode[];
  depth: number;
  /** Folder these rows sit in; "" for the root. */
  folder: string;
  selectedPath: string | null;
  expandedFolders: ReadonlySet<string>;
  readOnly: boolean;
  editing: SkillTreeEntryEditing;
};

export function SkillFileTreeRows(props: SkillFileTreeRowsProps) {
  const { nodes, depth, folder, editing } = props;
  const { entry } = editing;
  const newHere = entry?.mode === "new" && entry.folder === folder ? entry : null;

  return (
    <>
      {newHere ? (
        <NamingRow kind={newHere.kind} depth={depth} initialName="" editing={editing} />
      ) : null}
      {nodes.map((node) =>
        entry?.mode === "rename" && entry.path === node.path ? (
          <NamingRow
            key={`rename:${node.path}`}
            kind={node.kind}
            depth={depth}
            initialName={node.name}
            editing={editing}
          />
        ) : node.kind === "folder" ? (
          <FolderRow key={`folder:${node.path}`} node={node} {...props} />
        ) : (
          <FileRow key={`file:${node.path}`} node={node} {...props} />
        )
      )}
    </>
  );
}

function FolderRow({ node, ...props }: SkillFileTreeRowsProps & { node: SkillFileTreeNode }) {
  const { depth, expandedFolders, readOnly, editing } = props;
  const expanded = expandedFolders.has(node.path);
  return (
    <Box>
      <ListItemButton
        dense
        onClick={() => editing.clickFolder(node.path)}
        sx={{ pl: rowIndent(depth, "folder"), ...rowHoverSx }}
      >
        <IconButton size="small" edge="start" tabIndex={-1} disableRipple sx={{ p: 0.25, mr: 0.5 }}>
          {expanded ? <ExpandMore sx={{ fontSize: 18 }} /> : <ChevronRight sx={{ fontSize: 18 }} />}
        </IconButton>
        <EntryIcon kind="folder" />
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{ variant: "body2", noWrap: true }}
        />
        {readOnly ? null : (
          <RenameButton name={node.name} onClick={() => editing.startRename("folder", node.path)} />
        )}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <SkillFileTreeRows
          {...props}
          nodes={node.children ?? []}
          depth={depth + 1}
          folder={node.path}
        />
      </Collapse>
    </Box>
  );
}

function FileRow({ node, ...props }: SkillFileTreeRowsProps & { node: SkillFileTreeNode }) {
  const { depth, selectedPath, readOnly, editing } = props;
  const isEntrypoint = node.path === SKILL_ENTRYPOINT;
  return (
    <ListItemButton
      dense
      selected={selectedPath === node.path}
      onClick={() => void editing.openFile(node.path)}
      sx={{ pl: rowIndent(depth, "file"), ...rowHoverSx }}
    >
      <EntryIcon kind="file" />
      <ListItemText
        primary={node.name}
        primaryTypographyProps={{ variant: "body2", noWrap: true }}
      />
      {isEntrypoint ? (
        <Tooltip title="SKILL.md is required and it's where the agent starts">
          <InfoOutlined
            aria-label="About SKILL.md"
            sx={{ fontSize: 16, color: "text.secondary", ml: 0.5 }}
          />
        </Tooltip>
      ) : null}
      {readOnly || isEntrypoint ? null : (
        <RenameButton name={node.name} onClick={() => editing.startRename("file", node.path)} />
      )}
    </ListItemButton>
  );
}
